import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import * as callables from '../../services/dpRequestCallables';
import { dpRequestsKeys, DP_REQUESTS_QUERY_KEY } from './dpRequestsQueryKeys';

interface MutationMessages {
    success: string;
    error: string;
}

function useDpMutation<TVariables, TResult = unknown>(
    mutationFn: (variables: TVariables) => Promise<TResult>,
    messages: MutationMessages
) {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation({
        retry: false,
        mutationFn,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [DP_REQUESTS_QUERY_KEY] });
            toast.success(messages.success);
        },
        onError: (error: unknown) => {
            const description = error instanceof Error ? error.message : undefined;
            toast.error(messages.error, description);
        },
    });
}

export function useSubmitDataUpdate() {
    return useDpMutation(callables.submitDataUpdate, {
        success: 'Pedido enviado ao DP',
        error: 'Não foi possível enviar a atualização',
    });
}

export function useResubmitDataUpdate() {
    return useDpMutation(
        (input: { requestId: string; payload: callables.DataUpdatePayload }) =>
            callables.resubmitDataUpdate(input.requestId, input.payload),
        { success: 'Pedido reenviado ao DP', error: 'Não foi possível reenviar' }
    );
}

export function useCancelDataUpdate() {
    return useDpMutation(callables.cancelDataUpdate, {
        success: 'Pedido cancelado',
        error: 'Não foi possível cancelar',
    });
}

export function useDecideDataUpdate() {
    return useDpMutation(callables.decideDataUpdate, {
        success: 'Decisão registrada',
        error: 'Não foi possível registrar a decisão',
    });
}

export function useSubmitTicket() {
    return useDpMutation(callables.submitTicket, {
        success: 'Chamado aberto',
        error: 'Não foi possível abrir o chamado',
    });
}

export function usePostTicketMessage() {
    return useDpMutation(callables.postTicketMessage, {
        success: 'Mensagem enviada',
        error: 'Não foi possível enviar a mensagem',
    });
}

export function useResolveTicket() {
    return useDpMutation(callables.resolveTicket, {
        success: 'Chamado marcado como resolvido',
        error: 'Não foi possível resolver o chamado',
    });
}

export function useCancelTicket() {
    return useDpMutation(callables.cancelTicket, {
        success: 'Chamado cancelado',
        error: 'Não foi possível cancelar o chamado',
    });
}

export function useSubmitDayOff() {
    return useDpMutation(callables.submitDayOff, {
        success: 'Pedido enviado ao gestor',
        error: 'Não foi possível enviar o day-off',
    });
}

export function useCancelDayOff() {
    return useDpMutation(callables.cancelDayOff, {
        success: 'Day-off cancelado',
        error: 'Não foi possível cancelar o day-off',
    });
}

export function useMarkDayOffRegistered() {
    return useDpMutation(callables.markDayOffRegistered, {
        success: 'Marcado como lançado no ponto',
        error: 'Não foi possível marcar como lançado',
    });
}

export function useDecideDayOff() {
    return useDpMutation(callables.decideDayOff, {
        success: 'Decisão registrada',
        error: 'Não foi possível registrar a decisão',
    });
}

export function useSubmitOvertime() {
    return useDpMutation(callables.submitOvertime, {
        success: 'Hora extra enviada ao DP',
        error: 'Não foi possível enviar a hora extra',
    });
}

export function useCancelOvertime() {
    return useDpMutation(callables.cancelOvertime, {
        success: 'Pedido cancelado',
        error: 'Não foi possível cancelar',
    });
}

export function useDecideOvertime() {
    return useDpMutation(callables.decideOvertime, {
        success: 'Decisão registrada',
        error: 'Não foi possível registrar a decisão',
    });
}

export function useSubmitRaroInvest() {
    return useDpMutation(callables.submitRaroInvest, {
        success: 'Pedido do Raro Invest enviado',
        error: 'Não foi possível enviar o pedido',
    });
}

export function useCancelRaroInvest() {
    return useDpMutation(callables.cancelRaroInvest, {
        success: 'Pedido cancelado',
        error: 'Não foi possível cancelar',
    });
}

export function useDecideRaroInvest() {
    return useDpMutation(callables.decideRaroInvest, {
        success: 'Decisão registrada',
        error: 'Não foi possível registrar a decisão',
    });
}

export function useMarkRaroInvestReimbursed() {
    return useDpMutation(callables.markRaroInvestReimbursed, {
        success: 'Reembolso registrado',
        error: 'Não foi possível registrar o reembolso',
    });
}

export function useSubmitHealthPlan() {
    return useDpMutation(callables.submitHealthPlan, {
        success: 'Pedido enviado ao DP',
        error: 'Não foi possível enviar o pedido',
    });
}

export function useCancelHealthPlan() {
    return useDpMutation(callables.cancelHealthPlan, {
        success: 'Pedido cancelado',
        error: 'Não foi possível cancelar',
    });
}

export function useDecideHealthPlan() {
    return useDpMutation(callables.decideHealthPlan, {
        success: 'Decisão registrada e cadastro atualizado',
        error: 'Não foi possível registrar a decisão',
    });
}

export function useInvalidateDpRequests() {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: dpRequestsKeys.all });
}
